import React, {
  useCallback, useLayoutEffect, useEffect, useMemo,
} from 'react';
import { FormattedMessage } from 'react-intl';
import { useQuery } from '@apollo/client';
import { useModal } from 'mui-modal-provider';

import { IApiSettingsProps, ApiErrors } from './api-settings.models';
import { setCallback as setGraphqlCallback } from '../../../graphql/graphql-client';
import { setCallback as setRestCallback } from '../../../rest/rest-client';
import { setPromiseErrorCallback } from '../../../utils/promise/set-promise-error-callbacks';
import { useLogout } from '../../../graphql/user/actions/logout';
import {
  getTokenExpiredFromStorage,
  getTokenFromStorage,
  removeTokenExpiredFromStorage,
  setTokenExpiredToStorage,
} from '../../../utils/user';
import { useRefreshToken } from '../../../hooks/use-refresh-token';
import { GET_USERNAME } from '../../../graphql/profile/queries/get-username';
import { Username } from '../../../graphql/profile/models/get-profile.models';
import { GET_IS_USER_LOGGED_IN } from '../../../graphql';
import { useUnknownErrorModal, useSnackbar } from '../../../hooks';
import { LOGOUT_MESSAGE_TIME } from '../../../constants';

export const ApiSettings = ({ children }: IApiSettingsProps): JSX.Element => {
  const { data: { user: { isLoggedIn } } } = useQuery(GET_IS_USER_LOGGED_IN);
  const { data } = useQuery<{ profile: Username }>(GET_USERNAME, {
    skip: !isLoggedIn,
  });
  const { logout } = useLogout();
  const { enqueueSnackbar } = useSnackbar();
  const tokenExpired = useMemo(() => getTokenExpiredFromStorage(), []);
  const { refreshToken } = useRefreshToken();
  const token = getTokenFromStorage();
  const { showModal } = useModal();
  const showUnknownErrorModal = useUnknownErrorModal(showModal);
  const showTokenInvalidSnackbar = useCallback(() => {
    localStorage?.setItem('isTokenInvalidLogout', 'true');
  }, []);
  const handleTokenExpired = useCallback(async () => {
    if (!token) {
      removeTokenExpiredFromStorage();
      return;
    }

    try {
      const newToken = await refreshToken(data?.profile?.username || '');
      removeTokenExpiredFromStorage();
      return newToken;
    } catch {
      setTokenExpiredToStorage();
      logout();
    }
  }, [logout, token, refreshToken, setTokenExpiredToStorage]);

  useLayoutEffect(() => {
    setGraphqlCallback(ApiErrors.onTokenExpired, handleTokenExpired);
    setRestCallback(ApiErrors.onTokenExpired, handleTokenExpired);
    setGraphqlCallback(ApiErrors.onTokenInvalid, showTokenInvalidSnackbar);
    setRestCallback(ApiErrors.onTokenInvalid, showTokenInvalidSnackbar);
    setGraphqlCallback(ApiErrors.onUnknownErrorReceived, showUnknownErrorModal);
    setRestCallback(ApiErrors.onUnknownErrorReceived, showUnknownErrorModal);
    setPromiseErrorCallback(ApiErrors.anyError, showUnknownErrorModal);
  }, [showUnknownErrorModal, handleTokenExpired, showTokenInvalidSnackbar]);

  useEffect((): void => {
    if (localStorage?.getItem('isTokenInvalidLogout')) {
      enqueueSnackbar(
        <FormattedMessage id="invalid.token.logout" />,
        { variant: 'success', key: 'tokenInvalidSuccess', preventDuplicate: true, autoHideDuration: LOGOUT_MESSAGE_TIME },
      );
      localStorage?.removeItem('isTokenInvalidLogout');
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    if (tokenExpired) {
      enqueueSnackbar(
        <FormattedMessage id="login.errors.tokenExpired"/>,
        { variant: 'error', key: 'tokenExpiredError', preventDuplicate: true },
      );
      handleTokenExpired();
    }
  }, [tokenExpired, enqueueSnackbar, handleTokenExpired]);

  return children as JSX.Element;
};
